import React, {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useState
} from 'react';
import { makeStyles, Typography, CircularProgress } from '@material-ui/core';
import {
  CurrentStepIndexContext,
  SetCurrentStepIndexContext,
  StepperDataContext
} from '../../../Contexts/StepperContext';
import { writeNfcCard } from '../../../Utils/Nfc/nfcOperations';
import clsx from 'clsx';
import { CancelRounded, CheckCircleRounded } from '@material-ui/icons';
import {
  CompleteRechargeAPI,
  ConfirmRechargeAPI
} from '../../../API/PrepaidMeters/PrepaidMetersAPI';
import { ROUTE_NAMES } from '../../../Routes/Routes';
import { PrepaidMeterRechargeStep } from '../PrepaidMeter_enums';
import { history } from '../../../Routes/history';
import BaseButton from '../../../Components/Buttons/BaseButton';

const WriteCardStep = props => {
  const {
    setCurrentStep: setVisualStepperIndex,
    setNextButtonText,
    setGoBack,
    authToken,
    requestId,
    formId,
    setCanSubmit
  } = props;
  const classes = useStyles();
  const setCurrentStep = useContext(SetCurrentStepIndexContext);
  const currentStepIndex = useContext(CurrentStepIndexContext);
  const stepperData = useContext(StepperDataContext);

  const [loading, setLoading] = useState(true);
  const [isWriteSuccess, setIsWriteSuccess] = useState(null);
  const [error, setError] = useState();

  const onBackward = useCallback(() => {
    if (stepperData.prepaidMeterCard) {
      setCurrentStep(PrepaidMeterRechargeStep.CardData);
    } else {
      setCurrentStep(PrepaidMeterRechargeStep.ReadCard);
    }
  }, [setCurrentStep, stepperData]);

  const finishFlow = useCallback(() => {
    history.push(ROUTE_NAMES.home);
  }, []);

  const getRechargeToken = useCallback(async () => {
    setError(false);
    if (!requestId) {
      setError('Recarga inválida');
      return;
    }
    const { success, data, error: err } = await ConfirmRechargeAPI(
      authToken,
      requestId
    );
    if (!success) {
      setError(err);
      return;
    }
    const { rechargeToken, meterSerial } = data;
    writeNfcCard({ cardToken: rechargeToken, meterSerial });
  }, [setError, requestId, authToken]);

  useEffect(() => {
    setNextButtonText('Finalizar');
    setVisualStepperIndex(currentStepIndex);
    setGoBack({ action: onBackward });
    setCanSubmit(false);
    getRechargeToken();
  }, [
    setNextButtonText,
    setGoBack,
    onBackward,
    setVisualStepperIndex,
    currentStepIndex,
    getRechargeToken
  ]);

  useEffect(() => {
    const handleWriteResult = async event => {
      if (!event || !event.detail) {
        setLoading(false);
        setIsWriteSuccess(false);
        return;
      }

      setIsWriteSuccess(event.detail);

      if (event.detail) {
        setGoBack({ action: false });
        await CompleteRechargeAPI(authToken, requestId);
        setCanSubmit(true);
      }

      setLoading(false);
    };

    // expose global function for Android evaluateJavascript -> window.onNfcWriteResult(result)
    window.onNfcWriteResult = result => {
      window.dispatchEvent(
        new CustomEvent('nfcWriteResult', { detail: result })
      );
    };

    window.addEventListener('nfcWriteResult', handleWriteResult);

    return () => {
      window.removeEventListener('nfcWriteResult', handleWriteResult);
      if (window.onNfcWriteResult) {
        try {
          delete window.onNfcWriteResult;
        } catch (e) {
          window.onNfcWriteResult = undefined;
        }
      }
    };
  }, [setLoading, setIsWriteSuccess, authToken, requestId]);

  const ErrorState = ({ errorMessage, onRetry }) => (
    <>
      <Typography variant="h2">{errorMessage}</Typography>
      <BaseButton
        color="primary"
        variant="outlined"
        fullWidth
        onClick={onRetry}
        className={classes.retryButton}
      >
        Intentar nuevamente
      </BaseButton>
    </>
  );

  const SuccessState = () => (
    <div className={classes.cardResult}>
      <CheckCircleRounded
        className={clsx(classes.responseIcon, classes.successIcon)}
      />
      <Typography className={classes.infoText}>
        ¡Ya casi está! Ahora acerca tu tarjeta al medidor para activar tu saldo
        y empezar a usar tu gas
      </Typography>
    </div>
  );

  const FailedState = ({ onRetry }) => (
    <div className={classes.cardResult}>
      <CancelRounded
        className={clsx(classes.responseIcon, classes.errorIcon)}
      />
      <Typography className={classes.infoText} color="error">
        Recarga fallida
      </Typography>
      <BaseButton
        color="primary"
        variant="outlined"
        fullWidth
        onClick={onRetry}
        className={classes.retryButton}
      >
        Intentar nuevamente
      </BaseButton>
    </div>
  );

  const renderContent = () => {
    if (error) {
      return <ErrorState errorMessage={error} onRetry={getRechargeToken} />;
    }

    if (loading) {
      return <CircularProgress />;
    }

    return (
      <>
        <form id={formId} onSubmit={finishFlow} />
        {isWriteSuccess ? (
          <SuccessState />
        ) : (
          <FailedState onRetry={getRechargeToken} />
        )}
      </>
    );
  };

  return (
    <div className={classes.root}>
      <div
        className={clsx(classes.innerContentContainer, classes.centerContent)}
      >
        {renderContent()}
      </div>
    </div>
  );
};

const useStyles = makeStyles(theme => ({
  root: {
    '&:last-child': {
      marginBottom: theme.spacing(2)
    }
  },
  innerContentContainer: {
    maxWidth: 950,
    justifyItems: 'center',
    alignContent: 'center',
    placeSelf: 'center',
    // viewport - header - footer - padding - stepper
    height: 'calc(100vh - 64px - 84px - 32px - 80px - 40px)'
  },
  centerContent: {
    margin: '0 auto',
    justifyItems: 'center'
  },
  leftItem: {
    paddingRight: theme.spacing(2),
    [theme.breakpoints.down(theme.breakpoints.values.sm)]: {
      paddingRight: 0,
      paddingBottom: theme.spacing(2)
    }
  },
  infoText: {
    fontSize: 16,
    textAlign: 'center',
    marginBottom: 0
  },
  responseIcon: {
    width: '90px',
    height: '90px',
    display: 'block',
    justifySelf: 'center'
  },
  errorIcon: {
    color: theme.palette.error.dark
  },
  successIcon: {
    color: theme.palette.success.main
  },
  retryButton: {
    marginTop: '5px'
  }
}));

export default WriteCardStep;
